import React, { useState, useEffect } from 'react';
import * as storageService from '../services/storageService';

interface AdminNavbarUserProps {
    primaryColor?: string;
    className?: string;
    theme?: 'light' | 'dark';
    variant?: 'topbar' | 'sidebar';
    collapsed?: boolean;
}

export const AdminNavbarUser: React.FC<AdminNavbarUserProps> = ({ 
    primaryColor = '#4F46E5', 
    className = '',
    theme = 'light',
    variant = 'topbar',
    collapsed = false
}) => {
    const [name, setName] = useState<string>('');
    const [role, setRole] = useState<string>('');
    
    useEffect(() => {
        const loadData = () => {
            let foundName = '';
            let foundRole = '';

            // Intentar leer contexto de Super Admin / Group Admin
            const adminCtxRaw = localStorage.getItem('hospitalstatus_admin_ctx');
            if (adminCtxRaw) {
                try {
                    const adminCtx = JSON.parse(adminCtxRaw);
                    if (adminCtx.name) foundName = adminCtx.name;
                    if (adminCtx.role) foundRole = adminCtx.role;
                } catch (e) {}
            }
            
            // Si no se encontró, extraer contexto Staff (Hospital Admin / Staff)
            if (!foundName) {
                const authData = storageService.getAuthData() as any;
                if (authData?.staffName) foundName = authData.staffName;
                else if (authData?.name) foundName = authData.name;
                
                if (authData?.staffRole) foundRole = authData.staffRole;
                else if (authData?.role) foundRole = authData.role;
            }
            
            setName(foundName || 'Administrador');
            setRole(foundRole || '');
        };

        loadData();
        window.addEventListener('auth-updated', loadData);
        window.addEventListener('storage', loadData);
        
        return () => {
            window.removeEventListener('auth-updated', loadData);
            window.removeEventListener('storage', loadData);
        };
    }, []);

    // Extrae las iniciales del nombre completo para el avatar
    const getInitials = (fullName: string) => {
        if (!fullName) return 'A';
        const parts = fullName.trim().split(/\s+/);
        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        return fullName.substring(0, 2).toUpperCase();
    };

    const initials = getInitials(name);
    const nameParts = name.trim().split(/\s+/);
    let displayName = nameParts[0] || '';
    if (nameParts.length > 1 && (nameParts[0].endsWith('.') || ['Dr', 'Dra', 'Dr.', 'Dra.'].includes(nameParts[0].trim()))) {
        displayName = `${nameParts[0]} ${nameParts[1]}`;
    }

    const roleLabel = role ? role.replace(/_/g, ' ') : '';

    // ── SIDEBAR VARIANT ──
    if (variant === 'sidebar') {
        if (collapsed) {
            return (
                <div className={`flex justify-center py-3 ${className}`} title={name}>
                    <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-black text-white shadow-lg relative overflow-hidden transition-transform hover:scale-110 cursor-default"
                        style={{ background: `linear-gradient(135deg, ${primaryColor}, #818CF8)` }}
                    >
                        <span className="relative z-10 tracking-wide">{initials}</span>
                    </div>
                </div>
            );
        }

        return (
            <div className={`flex items-center gap-3 px-3 py-4 ${className}`}>
                <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-black text-white shadow-lg flex-shrink-0 relative overflow-hidden cursor-default"
                    style={{ background: `linear-gradient(135deg, ${primaryColor}, #818CF8)` }}
                >
                    <span className="relative z-10 tracking-wide">{initials}</span>
                </div>
                <div className="flex-1 min-w-0">
                    <p className={`text-sm font-bold leading-tight truncate ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>
                        {displayName}
                    </p>
                    {roleLabel && (
                        <p className={`text-[9px] font-bold uppercase tracking-widest mt-0.5 ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>
                            {roleLabel}
                        </p>
                    )}
                </div>
            </div>
        );
    }

    // ── TOPBAR VARIANT (Original) ──
    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Buenos días';
        if (hour < 19) return 'Buenas tardes';
        return 'Buenas noches';
    };

    const greeting = getGreeting();

    return (
        <div className={`flex items-center gap-3 ${className}`}>
           <div className="text-right hidden sm:block">
               <p className={`text-sm font-bold leading-none mb-1 ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>
                   {greeting},{' '}
                   <span 
                       style={{ color: primaryColor }} 
                       className={theme === 'dark' ? 'brightness-125 saturate-150' : ''}
                   >
                       {displayName}
                   </span>
               </p>
               {roleLabel && (
                   <p className={`text-[9px] font-bold uppercase tracking-widest ${theme === 'dark' ? 'text-slate-400' : 'text-slate-400'}`}>
                       {roleLabel}
                   </p>
               )}
           </div>
           
           <div 
               className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-black text-white shadow-lg border-2 border-white/20 relative overflow-hidden group cursor-default shadow-[0_4px_14px_rgba(0,0,0,0.1)] transition-transform hover:scale-105"
               style={{ 
                   background: `linear-gradient(135deg, ${primaryColor}, #818CF8)` 
               }}
               title={name}
           >
               {/* Resplandor interno glassmorphism */}
               <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
               <span className="relative z-10 tracking-widest ml-[2px]">{initials}</span>
           </div>
        </div>
    );
};
